import { useState } from "react";
import AviationSectorPicker from "../../../components/Sectors/AviationSectorPicker";
import FormSubmitButton from "../../../components/FormSubmitButton";
import SubFlowHeader from "../../../components/GoalSubflow/SubFlowHeader";
import OptionPills from "../../../components/GoalSubflow/OptionPills";
import { useGoalSubflow } from "./useGoalSubflow";
import { getOptions } from "../../registry";
import { t } from "../../../../../locales/t";

/**
 * "Finding your mentor" — the Be-Mentored sub-flow.
 *
 * An ordinary onboarding screen, the same shape as `GoalsStep`: it owns its
 * form state, its gate and its Continue, and the router resolves it directly at
 * `/onboarding/about-you/receive-mentorship`.
 *
 * What it does not decide is *which* questions are its to ask. `asks` comes
 * from `planSubFlows`, where the cross-awareness between sub-flows lives — give
 * it `{ sectors: false }` and the sector question is simply not asked, which is
 * how a dedup rule reaches the screen without the screen knowing the other four
 * exist.
 */
const GOAL = "receive_mentorship";

const ReceiveMentorship = () => {
  const { asks, index, total, answers, submit } = useGoalSubflow(GOAL);

  // Seeded from the flow so revisiting shows what was already answered.
  const [sectors, setSectors] = useState(
    () => answers.sectors ?? { primary: [], secondary: {} },
  );
  const [careerStage, setCareerStage] = useState(
    () => answers.careerStage ?? [],
  );

  // The stage question is optional, so it never gates. Sectors do: without one
  // there is nothing to match a mentor on.
  const canContinue = !asks.sectors || sectors.primary.length > 0;

  const handleContinue = () => {
    if (!canContinue) return;
    submit({
      ...(asks.sectors ? { sectors } : {}),
      ...(asks.careerStage ? { careerStage } : {}),
    });
  };

  return (
    <SubFlowHeader
      goal={GOAL}
      index={index}
      total={total}
      onSubmit={handleContinue}
    >
      {asks.sectors && (
        <AviationSectorPicker
          value={sectors}
          onChange={setSectors}
          mode={asks.sectors}
          label={t(`onboarding.subflow.${GOAL}.sectorsLabel`)}
          subtitle={t(`onboarding.subflow.${GOAL}.sectorsSubtitle`)}
        />
      )}

      {asks.careerStage && (
        <OptionPills
          multiple
          options={getOptions("careerStage")}
          value={careerStage}
          onChange={setCareerStage}
          label={t(`onboarding.subflow.${GOAL}.stageLabel`)}
          hint={t(`onboarding.subflow.${GOAL}.stageOptional`)}
        />
      )}

      <FormSubmitButton
        variant="contained"
        fullWidth
        disabledReason={!canContinue ? t("onboarding.subflow.incomplete") : ""}
        sx={{ py: 1.5 }}
      >
        {t("onboarding.subflow.continue")}
      </FormSubmitButton>
    </SubFlowHeader>
  );
};

export default ReceiveMentorship;
